import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ImagePlus, Loader2, Trash2, RefreshCw } from 'lucide-react';
import { ACCEPTED_IMAGE_TYPES, MAX_IMAGE_BYTES, readImagePayload, ImageUploadPayload } from './imageFile';

export type ImageUploadVariant = 'logo' | 'cover' | 'merchantCover' | 'featuredItem' | 'banner';

/**
 * Aspect ratio published for each slot. It lives in the design system because
 * the merchant must crop here exactly what the customer sees in the app and on
 * the site - a per-page ratio would let the same image be cut two ways.
 */
const VARIANT_ASPECT: Record<ImageUploadVariant, string> = {
  logo: '1 / 1',
  /*
    `cover` é a capa do ANÚNCIO e da EXPERIÊNCIA. 16:9 porque é assim que os
    clientes desenham: `.promo-card__media` no site e `AspectRatio(16/9)` no
    `promo_card.dart` e no `experience_card.dart` do app.

    O item em destaque saiu daqui - este comentário dizia que ele também era
    16:9 e estava errado. Ver `featuredItem` abaixo.
  */
  cover: '16 / 9',
  /*
    `merchantCover` é a capa da LOJA, e ela é 16:10 (card #302).

    Ficou variante à parte, e não um ajuste em `cover`, porque as duas capas
    são desenhadas em proporções diferentes de verdade: mexer em `cover` para
    acertar a loja passaria a recortar errado a capa do anúncio, que é 16:9
    onde quer que ela apareça.

    O 16:10 não é escolha desta camada: é o que o site renderiza em
    `.business-card__media`, o cartão da vitrine, da busca, da cidade e das
    coleções - exatamente os lugares onde a capa da loja é vista. O app não
    tem proporção nomeada para ela (recorta em caixas de 104x104 e de 116px de
    altura), então não desempata. A moldura aqui dizia 16:9 e o texto de ajuda
    do backoffice dizia 16:10; quem tinha razão era o texto, e é a moldura que
    muda.
  */
  merchantCover: '16 / 10',
  /*
    `featuredItem` é a foto do produto ou serviço em destaque (card #218), e ela
    é **4:3**.

    Nasceu usando `cover`, e isso era um defeito de verdade, não uma diferença
    de gosto: o lojista enquadrava a foto em 16:9 e os dois clientes a
    recortavam em 4:3 - `.featured-card__media` no site e `AspectRatio(4/3)` no
    `featured_items_section.dart` do app. O que ele centralizava na moldura
    perdia as laterais na tela. Mesma classe do #302, campo diferente, apanhado
    ao conferir aquele.

    Como no #302, a correção é variante à parte e não ajuste em `cover`: as
    proporções são diferentes de verdade, e mexer em `cover` passaria a
    recortar errado a capa do anúncio.
  */
  featuredItem: '4 / 3',
  // O banner é bem mais largo que a capa de propósito: a moldura precisa mostrar
  // desde já que a arte do cartão não serve aqui, senão os dois campos acabam
  // recebendo a mesma imagem.
  banner: '3 / 1',
};

/** Altura das molduras no modo compacto. Ver a prop `compact`. */
const COMPACT_HEIGHT = 88;

const VARIANT_HINT: Record<ImageUploadVariant, string> = {
  logo: 'Proporção 1:1',
  cover: 'Proporção 16:9',
  merchantCover: 'Proporção 16:10',
  featuredItem: 'Proporção 4:3',
  banner: 'Proporção 3:1',
};

const VARIANT_CALL_TO_ACTION: Record<ImageUploadVariant, string> = {
  logo: 'Enviar logomarca',
  cover: 'Enviar imagem de capa',
  merchantCover: 'Enviar imagem de capa',
  featuredItem: 'Enviar foto',
  banner: 'Enviar banner',
};

interface ImageUploadProps {
  label?: string;
  /** Current stored image URL (or undefined when empty). */
  value?: string;
  /** Called with the new URL after a successful upload, or undefined when removed. */
  onChange: (url: string | undefined) => void;
  /** Uploads the picked file and resolves to its public URL. */
  onUpload: (payload: ImageUploadPayload) => Promise<string>;
  /** Visual shape: compact square logo or wide cover banner. */
  variant?: ImageUploadVariant;
  /**
   * Molduras baixas, com a largura saindo da proporcao em vez de ocupar a
   * linha inteira.
   *
   * No tamanho normal a moldura e desenhada na largura DISPONIVEL: num modal
   * de 1040px a capa 16:9 vira um bloco de 756x425 e o banner 3:1 um de
   * 1000x333 (medido). So o passo de Marca dava 944px de conteudo e rolava em
   * QUALQUER tela, ate em 1920x1080. Com a altura fixa as tres molduras cabem
   * lado a lado e o passo inteiro cai para 221px.
   *
   * O ganho nao e so espaco: alinhadas pela mesma altura, as larguras
   * diferentes mostram as tres proporcoes de uma olhada -- que e justamente o
   * que o operador precisa entender para nao subir a mesma arte nos tres.
   */
  compact?: boolean;
  /**
   * Troca só o convite escrito dentro da moldura vazia.
   *
   * A `variant` continua mandando na PROPORÇÃO - é decisão do design system, e
   * é por isso que ela é fechada. Mas o texto padrão dela nomeia o campo do
   * perfil da loja ("Enviar imagem de capa"), e a foto de um produto em
   * destaque (card #218) reusa a mesma proporção 16:9 sem ser capa de nada.
   * Sem isto a moldura convidaria o lojista a enviar a capa da loja no campo
   * do cappuccino.
   */
  callToAction?: string;
  helpText?: string;
  disabled?: boolean;
  onError?: (message: string) => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  width: 100%;
`;

const Label = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
`;

const Frame = styled.div<{ $variant: ImageUploadVariant; $disabled?: boolean; $compact?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px dashed ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  transition: border-color 0.2s, box-shadow 0.2s;
  aspect-ratio: ${({ $variant }: { $variant: ImageUploadVariant }) => VARIANT_ASPECT[$variant]};
  /*
   * No modo compacto quem manda e a ALTURA: a largura sai da proporcao
   * sozinha. O align-self nao e enfeite -- o container e um flex column, e sem
   * ele a largura automatica esticaria a moldura ate a borda, a proporcao
   * levaria a altura junto e o ganho evaporava.
   * (Sem crase neste comentario de proposito: ele mora dentro de um template
   * literal, e uma crase aqui fecha a string do styled-components.)
   */
  ${(p) =>
    p.$compact
      ? `height: ${COMPACT_HEIGHT}px; width: auto; align-self: flex-start;`
      : `width: ${p.$variant === 'logo' ? '128px' : '100%'};`}

  &:hover {
    border-color: ${(p) => (p.$disabled ? p.theme.border : p.theme.primary)};
  }
`;

const Preview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlaceholderHint = styled.small`
  font-size: 0.7rem;
  font-weight: 600;
  opacity: 0.75;
`;

/*
 * No modo compacto a moldura tem 88px de altura -- e a menor delas, a da logo,
 * so 88px de largura. O convite em tamanho normal (icone de 22, chamada de
 * 0.8rem e a linha da proporcao) mede mais que isso e seria cortado pelo
 * overflow: hidden da moldura. Por isso o compacto encolhe o icone, o texto e
 * o respiro: o que sai de dentro da moldura continua dito do lado de fora --
 * o rotulo em cima e a proporcao no texto de ajuda embaixo.
 */
const Placeholder = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${(p) => (p.$compact ? '0.2rem' : '0.4rem')};
  color: ${(p) => p.theme.textMuted};
  font-size: ${(p) => (p.$compact ? '0.7rem' : '0.8rem')};
  padding: ${(p) => (p.$compact ? '0.35rem' : '0.75rem')};
  text-align: center;
  ${(p) => (p.$compact ? 'line-height: 1.2;' : '')}
`;

const Overlay = styled.div<{ $compact?: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: rgba(0, 0, 0, 0.45);
  opacity: 0;
  transition: opacity 0.2s;

  ${Frame}:hover & {
    opacity: 1;
  }

  /*
   * Card #303. Telas de toque nao tem :hover -- o ponteiro e "coarse" e nao
   * existe evento equivalente a "passando o mouse" antes do toque em si. Sem
   * esta query "Remover" fica inalcancavel ali (nada nunca poe opacity em 1)
   * e "Trocar" so funciona por acidente, porque o onClick da propria moldura
   * abre o seletor por baixo. (hover: none) pergunta "este ponteiro tem hover
   * de verdade", entao uma janela estreita de desktop com mouse nao muda --
   * e sobre o tipo de ponteiro, nao a largura da tela.
   */
  @media (hover: none) {
    opacity: 1;
  }

  /*
   * A moldura compacta de 88px (ver COMPACT_HEIGHT) nao tem espaco para dois
   * botoes "Trocar"/"Remover" lado a lado com texto -- o slot da logo e
   * 88x88. Empilhar as duas acoes uma em cima da outra preenchendo a moldura
   * inteira transforma cada metade num alvo de toque de verdade, em vez de
   * uma fileira apertada de botoes minusculos.
   */
  ${(p) =>
    p.$compact &&
    `
    flex-direction: column;
    align-items: stretch;
    justify-content: stretch;
    gap: 1px;
  `}
`;

const OverlayButton = styled.button<{ $compact?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  border: none;
  border-radius: ${(p) => p.theme.radius};
  padding: 0.4rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #fff;
  background: rgba(255, 255, 255, 0.18);
  cursor: pointer;

  /* :active entra pro toque -- nao ha :hover pra dar feedback de que o botao respondeu. */
  &:hover,
  &:active {
    background: rgba(255, 255, 255, 0.3);
  }

  /*
   * Compacto: so o icone, cada botao ocupando metade da moldura de 88px em
   * vez da pilula icone+rotulo de cima. Nao ha espaco pro texto "Trocar"/
   * "Remover" nesse tamanho (ver comentario do Overlay); o icone carrega a
   * acao e o aria-label no render mantem o nome pra leitor de tela.
   */
  ${(p) =>
    p.$compact &&
    `
    flex: 1;
    padding: 0;
    border-radius: 0;
  `}
`;

const Spinner = styled(Loader2)`
  animation: ku-spin 0.8s linear infinite;
  color: ${(p) => p.theme.primary};
  @keyframes ku-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

/**
 * Card #304. Preview otimista: enquanto `uploading`, a moldura mostra a
 * imagem escolhida (local, via `URL.createObjectURL`) em vez de placeholder
 * vazio. O spinner continua visible por cima -- sem ele, um envio lento fica
 * indistinguivel de um envio pronto. `$overPreview` só entra quando há
 * imagem embaixo: sem preview (o `uploading` sem `previewUrl` que não deveria
 * acontecer, mas é o fallback seguro) o fundo da própria `Frame` já basta.
 */
const SpinnerOverlay = styled.div<{ $overPreview?: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  ${(p) => (p.$overPreview ? 'background: rgba(0, 0, 0, 0.35);' : '')}
`;

const HelpText = styled.small<{ $isError?: boolean }>`
  font-size: 0.75rem;
  color: ${(p) => (p.$isError ? p.theme.error : p.theme.textMuted)};
`;

const HiddenInput = styled.input`
  display: none;
`;

/**
 * O componente de imagem unica do design system: logomarca (1:1), capa de
 * conteudo (16:9), capa da loja (16:10) e banner (3:1). Le o arquivo em base64, delega o envio ao `onUpload` e
 * guarda a URL devolvida. So usa tokens do tema, nenhum controle HTML cru.
 *
 * ## Por que existe um so (card #301)
 *
 * Ate esta leva o design system tinha DOIS componentes para a mesma tarefa:
 * este e o `ImageUploader`. O card #300 provou o custo - o mesmo ajuste visual
 * (as molduras compactas de 88px de altura) teve de ser escrito duas vezes,
 * uma em cada componente, porque as duas telas que precisavam dele usavam
 * componentes diferentes. O `ImageUploader` saiu de circulacao e este absorveu
 * o unico chamador que ele tinha: a aba Imagens do painel do lojista
 * (`apps/admin/src/pages/ProfilePage.tsx`), 3 campos.
 *
 * A direcao - este absorver o outro, e nao o contrario - saiu da contagem e do
 * contrato. Da contagem: 4 dos 7 campos ja eram deste componente, entao migrar
 * 3 chamadas era menos risco que migrar 4. Do contrato: e este que tem
 * variantes, rotulo, estado desabilitado e o spinner de envio, e e o
 * "sobe na hora e devolve a URL" dele que as tres telas de fato querem. O
 * `ImageUploader` devolvia o `File` cru, e o `ProfilePage` reconstruia este
 * mesmo contrato a mao (um `handleImageField` que chamava `readImagePayload` e
 * depois `uploadMerchantImage`) so para poder guardar a URL no final.
 *
 * ## As quatro divergencias, resolvidas uma a uma
 *
 * 1. **Quem envia.** Fica o `onUpload(payload) => Promise<url>`. O `File` cru
 *    nao e devolvido a ninguem: as tres telas mandam o mesmo payload para o
 *    mesmo endpoint, e deixar cada uma montar o envio foi exatamente como o
 *    `ProfilePage` acabou duplicando a leitura do arquivo.
 * 2. **Proporcao.** Fica a `variant` fechada (`VARIANT_ASPECT`), nao uma
 *    string livre de proporcao. A proporcao e decisao do design system, nao da
 *    tela: com string livre a mesma logomarca pode ser cortada 1:1 numa tela e
 *    4:3 na outra, e o lojista nao entende por que a arte "mudou". As tres
 *    proporcoes que o `ImageUploader` recebia ('1:1', '16:9', '3:1') sao
 *    exatamente as tres variantes daqui, entao nenhum chamador ficou sem.
 * 3. **Remover.** Fica o overlay de hover (Trocar/Remover), nao o botao
 *    redondo vermelho sempre visivel. Ele cobre as DUAS acoes - e trocar e o
 *    que mais se faz - e nao tapa um canto da miniatura de 88px do modo
 *    compacto. O preco e conhecido: em tela de toque nao ha hover. Isso nao
 *    foi resolvido aqui, virou card proprio (citado no comentario do #301).
 * 4. **Preview local.** NAO veio junto, de proposito. O `ImageUploader`
 *    pintava o base64 na moldura antes de o envio terminar; aqui a moldura
 *    mostra o spinner ate a URL chegar. Trazer o preview otimista mudaria o
 *    estado "enviando" dos 7 campos, inclusive os 4 que o #301 promete nao
 *    mexer - tambem virou card proprio.
 *
 * O que veio junto do `ImageUploader` foi so a recusa de arquivo que nao e
 * imagem (ver `handleFile`): o `accept` do input filtra o seletor, mas nao
 * impede quem troca o filtro para "todos os arquivos".
 *
 * A `GalleryInput` NAO entrava nesta conta: ela e a grade de varias imagens,
 * nao usa nenhum dos dois por dentro (fala direto com `./imageFile`) e ficou
 * como estava.
 */
export const ImageUpload: React.FC<ImageUploadProps> = ({
  label,
  value,
  onChange,
  onUpload,
  variant = 'logo',
  compact = false,
  callToAction,
  helpText,
  disabled,
  onError,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  // Card #304: prévia local (URL.createObjectURL) enquanto a imagem sobe.
  // O ref guarda a URL atual pra poder revogar (evita vazamento de memória);
  // o objectUrl não pede revogação em erro de rede, mas pede sempre que
  // trocamos de imagem ou desmontamos com uma prévia pendurada.
  const objectUrlRef = useRef<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const clearPreview = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setPreviewUrl(null);
  };

  useEffect(() => () => clearPreview(), []);

  const openPicker = () => {
    if (disabled || uploading) return;
    inputRef.current?.click();
  };

  const fail = (message: string) => {
    setLocalError(message);
    onError?.(message);
  };

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      fail('A imagem deve ter no máximo 5 MB.');
      return;
    }
    // Herdado do `ImageUploader` ao unificar (card #301): o `accept` do input
    // só filtra o que o seletor mostra, e quem troca o filtro para "todos os
    // arquivos" consegue escolher um PDF. Sem esta guarda o arquivo virava
    // base64, subia e voltava como "Não foi possível enviar a imagem." - erro
    // certo, motivo errado.
    if (!file.type.startsWith('image/')) {
      fail('O arquivo selecionado não é uma imagem.');
      return;
    }
    setLocalError(null);
    // Prévia de uma tentativa anterior que falhou e ficou pendurada na
    // moldura (ver o `catch` abaixo) -- troca de arquivo descarta a antiga
    // antes de criar a nova, senão a de baixo vaza.
    clearPreview();
    const objectUrl = URL.createObjectURL(file);
    objectUrlRef.current = objectUrl;
    setPreviewUrl(objectUrl);
    setUploading(true);
    try {
      const payload = await readImagePayload(file);
      const url = await onUpload(payload);
      onChange(url);
      // Sucesso: a URL definitiva (prop `value`) assume o lugar da prévia local.
      clearPreview();
    } catch {
      fail('Não foi possível enviar a imagem.');
      // Erro: mantém a prévia na moldura com a mensagem em vermelho embaixo
      // (via HelpText) -- menos abrupto que esvaziar a moldura de novo, e
      // deixa claro qual imagem falhou. `clearPreview` só roda na próxima
      // escolha de arquivo, ou se o componente desmontar antes disso.
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange(undefined);
  };

  return (
    <Container>
      {label && <Label>{label}</Label>}
      <Frame $variant={variant} $compact={compact} $disabled={disabled} onClick={openPicker}>
        {uploading ? (
          <>
            {previewUrl && <Preview src={previewUrl} alt={label || 'Imagem'} />}
            <SpinnerOverlay $overPreview={!!previewUrl}>
              <Spinner size={26} />
            </SpinnerOverlay>
          </>
        ) : previewUrl ? (
          // Prévia de uma tentativa que falhou (ver handleFile) -- sem overlay
          // de Trocar/Remover: não é um valor salvo, é só o que o lojista
          // acabou de escolher. Clicar na moldura (onClick do Frame) tenta de
          // novo, e HelpText já mostra o motivo do erro embaixo.
          <Preview src={previewUrl} alt={label || 'Imagem'} />
        ) : value ? (
          <>
            <Preview src={value} alt={label || 'Imagem'} />
            {!disabled && (
              <Overlay $compact={compact}>
                <OverlayButton
                  type="button"
                  onClick={openPicker}
                  $compact={compact}
                  aria-label={compact ? 'Trocar imagem' : undefined}
                >
                  <RefreshCw size={14} />
                  {!compact && ' Trocar'}
                </OverlayButton>
                <OverlayButton
                  type="button"
                  onClick={handleRemove}
                  $compact={compact}
                  aria-label={compact ? 'Remover imagem' : undefined}
                >
                  <Trash2 size={14} />
                  {!compact && ' Remover'}
                </OverlayButton>
              </Overlay>
            )}
          </>
        ) : (
          <Placeholder $compact={compact}>
            <ImagePlus size={compact ? 18 : 22} />
            {callToAction || VARIANT_CALL_TO_ACTION[variant]}
            {!compact && <PlaceholderHint>{VARIANT_HINT[variant]}</PlaceholderHint>}
          </Placeholder>
        )}
      </Frame>
      <HiddenInput
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        onChange={handleFile}
        disabled={disabled}
      />
      {(localError || helpText) && <HelpText $isError={!!localError}>{localError || helpText}</HelpText>}
    </Container>
  );
};

export default ImageUpload;
